import { useState } from 'react';
import { useCollectionOptions } from '../hooks';

interface Props {
  scope: 'ALL' | 'SELECTED';
  selected: string[];
  onScopeChange: (scope: 'ALL' | 'SELECTED') => void;
  onSelectedChange: (codes: string[]) => void;
}

/** Explicit All/Selected choice so "empty list = every product" is never a surprise. */
export function CollectionScopePicker({ scope, selected, onScopeChange, onSelectedChange }: Props) {
  const { data: options = [], isLoading, isError } = useCollectionOptions();
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const visible = options.filter(
    (o) => !q || o.code.toLowerCase().includes(q) || o.displayName.toLowerCase().includes(q),
  );
  // Codes already on the coupon that the first-100 option list doesn't include still render, so they can be removed.
  const unknown = selected.filter((code) => !options.some((o) => o.code === code));

  const toggle = (code: string) =>
    onSelectedChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);

  return (
    <div className="space-y-2">
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="radio" checked={scope === 'ALL'} onChange={() => onScopeChange('ALL')} /> All products
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" checked={scope === 'SELECTED'} onChange={() => onScopeChange('SELECTED')} /> Only
          selected products
        </label>
      </div>
      {scope === 'SELECTED' && (
        <div className="rounded-lg border border-border-subtle p-2 space-y-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter products"
            aria-label="Filter products"
            className="w-full px-2 py-1.5 rounded-md bg-surface-container-lowest border border-border-subtle text-xs focus:outline-none"
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {isLoading && <p className="text-xs text-on-surface-variant">Loading products…</p>}
            {isError && <p className="text-xs text-red-300">Couldn't load products.</p>}
            {[...unknown.map((code) => ({ code, displayName: code, isActive: true })), ...visible].map((o) => (
              <label key={o.code} className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={selected.includes(o.code)} onChange={() => toggle(o.code)} />
                <span>
                  {o.displayName} <span className="font-mono text-on-surface-variant">({o.code})</span>
                </span>
                {!o.isActive && <span className="text-[10px] uppercase text-amber-300">inactive</span>}
              </label>
            ))}
          </div>
          <p className="text-[10px] text-on-surface-variant">{selected.length} selected</p>
        </div>
      )}
    </div>
  );
}
